// src/components/Header.tsx
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Flex, Button, Menu, MenuButton, MenuItem, Text, View, Image } from '@aws-amplify/ui-react';
import { useAuth } from '../AuthContext';
import { Menu as MenuIcon, X } from 'lucide-react';
import './Header.css';

const Header: React.FC = () => {
  const { user, signOut, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const handleSignIn = () => {
    navigate('/login');
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  return (
    <header className="header cinematic-header">
      <Flex direction="row" justifyContent="space-between" alignItems="center" padding="1rem" width="100%">
        {/* Logo */}
        <Link to="/" className="logo-link" onClick={closeMobileMenu}>
          <Flex alignItems="center">
            <Image
              src="/banjara-logo-dark-bg.png"
              alt="Banjara Logo"
              className="header-logo"
              style={{
                height: 'auto',
                width: '180px',
                objectFit: 'contain' // This ensures the logo is fully visible
              }}
            />
          </Flex>
        </Link>

        {/* Desktop Navigation */}
        <Flex className="desktop-nav">
          {isAuthenticated && (
            <>
              <Link to="/trips" className="nav-link">MY ALBUMS</Link>
              <Link to="/trips/new" className="nav-link">CREATE ALBUM</Link>
            </>
          )}
        </Flex>

        {/* User Menu (Desktop) */}
        <Flex className="desktop-user-menu">
          {isAuthenticated ? (
            <Menu
              trigger={
                <MenuButton variation="menu">
                  <Flex alignItems="center">
                    <Text fontWeight="500" marginRight="0.5rem" className="uppercase tracking-wide">
                      {user?.name || 'User'}
                    </Text>
                  </Flex>
                </MenuButton>
              }
            >
              <MenuItem onClick={() => navigate('/profile')}>Profile</MenuItem>
              <MenuItem onClick={handleSignOut}>Sign Out</MenuItem>
            </Menu>
          ) : (
            <Button 
              onClick={handleSignIn} 
              variation="primary"
              className="sign-in-btn"
            >
              Sign In
            </Button>
          )}
        </Flex>

        {/* Mobile Menu Toggle */}
        <Button
          className="mobile-menu-toggle"
          onClick={toggleMobileMenu}
          variation="link"
        >
          {mobileMenuOpen ? <X size={24} /> : <MenuIcon size={24} />}
        </Button>
      </Flex>

      {/* Mobile Menu (Slide-down) */}
      {mobileMenuOpen && (
        <View className="mobile-menu">
          <Flex direction="column" gap="1rem" padding="1rem">
            {isAuthenticated ? (
              <>
                <Link
                  to="/trips"
                  className="mobile-nav-link"
                  onClick={closeMobileMenu}
                >
                  MY ALBUMS
                </Link>
                <Link
                  to="/trips/new"
                  className="mobile-nav-link"
                  onClick={closeMobileMenu}
                >
                  CREATE ALBUM
                </Link>
                <Link
                  to="/profile"
                  className="mobile-nav-link"
                  onClick={closeMobileMenu}
                >
                  PROFILE
                </Link>
                <Button
                  onClick={() => {
                    handleSignOut();
                    closeMobileMenu();
                  }}
                  variation="destructive"
                  isFullWidth
                >
                  SIGN OUT
                </Button>
              </>
            ) : (
              <Button
                onClick={() => {
                  handleSignIn();
                  closeMobileMenu();
                }}
                variation="primary"
                isFullWidth
              >
                SIGN IN
              </Button>
            )}
          </Flex>
        </View>
      )}
    </header>
  );
};

export default Header;